import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Share, PlusSquare, Smartphone, CheckCircle2, MoreVertical } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  isStandalone, isIOS, subscribeToInstallPrompt, triggerInstall, getInstallPrompt,
} from "../lib/pwa";
import { toast } from "sonner";

/**
 * InstallButton — always visible unless the app is already installed.
 * Click behaviour:
 *  - beforeinstallprompt captured (Chrome/Edge/Android) → native install dialog
 *  - iOS Safari → open modal with Add-to-Home-Screen steps
 *  - otherwise (Firefox, unsupported) → open modal with generic browser menu tips
 */
export default function InstallButton({ className = "", variant = "primary" }) {
  const [canPrompt, setCanPrompt] = useState(!!getInstallPrompt());
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const unsub = subscribeToInstallPrompt((p) => setCanPrompt(!!p));
    return () => { unsub && unsub(); };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    if (canPrompt) {
      const res = await triggerInstall();
      if (res?.outcome === "accepted") {
        toast.success("Installing BrimiAI · ScienceLab…");
        setInstalled(true);
        return;
      }
      if (res?.outcome === "dismissed") return;
    }
    // no native prompt available → show manual steps
    setShowHelp(true);
  };

  const iOS = isIOS();
  const styles = variant === "primary"
    ? "brut-btn rounded-full bg-[#FFE066] text-[#1A1A1A] hover:bg-[#FFE066]"
    : "brut-btn rounded-full bg-white text-[#1A1A1A] hover:bg-white";

  return (
    <>
      <Button
        onClick={onClick}
        data-testid="install-app-btn"
        className={`${styles} ${className}`}>
        <Smartphone size={18} className="mr-2" strokeWidth={2.5}/>
        Install App
      </Button>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="brut-border brut-shadow bg-white rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-black text-2xl">
              {iOS ? "Install on iPhone / iPad" : "Install BrimiAI · ScienceLab"}
            </DialogTitle>
            <DialogDescription>
              {iOS
                ? "Add BrimiAI · ScienceLab to your home screen — takes 5 seconds."
                : "Use your browser's install option to add BrimiAI · ScienceLab to your device."}
            </DialogDescription>
          </DialogHeader>

          {iOS ? (
            <ol className="space-y-3 mt-2">
              <li className="brut-border p-3 bg-[#FFF5D1] flex items-start gap-3">
                <div className="w-8 h-8 brut-border bg-white flex items-center justify-center font-black flex-none">1</div>
                <div className="flex-1">
                  Tap the <b>Share</b> button
                  <Share size={16} className="inline mx-1" strokeWidth={2.5}/>
                  at the bottom of Safari.
                </div>
              </li>
              <li className="brut-border p-3 bg-[#E9E6FA] flex items-start gap-3">
                <div className="w-8 h-8 brut-border bg-white flex items-center justify-center font-black flex-none">2</div>
                <div className="flex-1">
                  Scroll down and tap <b>Add to Home Screen</b>
                  <PlusSquare size={16} className="inline mx-1" strokeWidth={2.5}/>.
                </div>
              </li>
              <li className="brut-border p-3 bg-[#B8E6C6] flex items-start gap-3">
                <div className="w-8 h-8 brut-border bg-white flex items-center justify-center font-black flex-none">3</div>
                <div className="flex-1">
                  Tap <b>Add</b> in the top-right corner. Done!
                  <CheckCircle2 size={16} className="inline ml-1" strokeWidth={2.5}/>
                </div>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 mt-2">
              <li className="brut-border p-3 bg-[#FFF5D1] flex items-start gap-3">
                <div className="w-8 h-8 brut-border bg-white flex items-center justify-center font-black flex-none">1</div>
                <div className="flex-1">
                  Tap the browser menu <MoreVertical size={16} className="inline" strokeWidth={2.5}/> (top-right on Chrome / Edge).
                </div>
              </li>
              <li className="brut-border p-3 bg-[#E9E6FA] flex items-start gap-3">
                <div className="w-8 h-8 brut-border bg-white flex items-center justify-center font-black flex-none">2</div>
                <div className="flex-1">
                  Choose <b>Install app</b> (desktop) or <b>Add to Home Screen</b> (Android).
                </div>
              </li>
              <li className="brut-border p-3 bg-[#B8E6C6] flex items-start gap-3">
                <div className="w-8 h-8 brut-border bg-white flex items-center justify-center font-black flex-none">3</div>
                <div className="flex-1">
                  Confirm <b>Install</b>. The app now opens like a native app.
                  <CheckCircle2 size={16} className="inline ml-1" strokeWidth={2.5}/>
                </div>
              </li>
            </ol>
          )}

          <div className="text-xs text-neutral-600 mt-2">
            {iOS
              ? "Note: On iOS this must be done from Safari (Chrome for iOS doesn't support Add-to-Home-Screen)."
              : "Tip: Chrome, Edge, Brave and Samsung Internet support one-click install. Firefox users can bookmark the site instead."}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
